/**
 * Extract the agent-memory signals from a message's annotations. The ai
 * service streams them as `data-memory` parts (see apps/ai memory-signal.ts);
 * Messages.client surfaces every data part's payload as an annotation, so the
 * signals arrive here as `{ type: 'memory', kind: 'recall' | 'saved', ... }`.
 */

export interface MemoryRecallAnnotation {
  type: 'memory';
  kind: 'recall';
  memories: number;
  project: number;
  skills: boolean;
}

export interface MemorySavedAnnotation {
  type: 'memory';
  kind: 'saved';
  memories: { key: string; kind: string }[];
  project: { key: string; kind: string }[];
  skills: { slug: string; title: string }[];
}

export interface MemorySignals {
  recall?: MemoryRecallAnnotation;
  saved?: MemorySavedAnnotation;
}

export function extractMemorySignals(annotations: readonly unknown[] | undefined): MemorySignals {
  const signals: MemorySignals = {};

  for (const ann of annotations ?? []) {
    if (!ann || typeof ann !== 'object' || (ann as { type?: unknown }).type !== 'memory') {
      continue;
    }

    const candidate = ann as { kind?: unknown };

    // Last one of each kind wins (a reloaded chat replays persisted parts in order).
    if (candidate.kind === 'recall') {
      signals.recall = ann as MemoryRecallAnnotation;
    } else if (candidate.kind === 'saved') {
      const saved = ann as MemorySavedAnnotation;

      if ((saved.memories?.length ?? 0) + (saved.project?.length ?? 0) + (saved.skills?.length ?? 0) > 0) {
        signals.saved = saved;
      }
    }
  }

  return signals;
}

/**
 * Stable identity for a recall signal — its payload (memory count, project-note
 * count, whether a skill was recalled). Used to suppress duplicate "Using
 * memories" entries across consecutive assistant turns: the chip only re-renders
 * when this key changes (see Messages.client).
 */
export function recallKey(recall: MemoryRecallAnnotation): string {
  return `${recall.memories}|${recall.project}|${recall.skills ? 1 : 0}`;
}

/** One-line human summary of a recall signal, e.g. "3 memories · 1 project note · a learned skill". */
export function recallSummary(recall: MemoryRecallAnnotation): string {
  const parts: string[] = [];

  if (recall.memories > 0) {
    parts.push(`${recall.memories} ${recall.memories === 1 ? 'memory' : 'memories'}`);
  }

  if (recall.project > 0) {
    parts.push(`${recall.project} project ${recall.project === 1 ? 'note' : 'notes'}`);
  }

  if (recall.skills) {
    parts.push('a learned skill');
  }

  return parts.join(' · ');
}
